import { __awaiter, __extends, __generator } from "tslib";
import { isDefined, getEventPath } from '@tyler-components-web/core';
import { BaseSelectFoundation } from '../core';
import { SELECT_CONSTANTS } from './select-constants';
/**
 * The foundation class behind the `<tcw-select>` component.
 */
var SelectFoundation = /** @class */ (function (_super) {
    __extends(SelectFoundation, _super);
    function SelectFoundation(adapter) {
        var _this = _super.call(this, adapter) || this;
        _this._label = '';
        _this._disabled = false;
        _this._invalid = false;
        _this._required = false;
        _this._floatLabelType = 'default';
        _this._density = 'default';
        _this._inset = false;
        _this._hasFocus = false;
        _this._leadingChangeListener = function (evt) { return _this._onLeadingSlotChanged(evt); };
        _this._addonEndChangeListener = function (evt) { return _this._onAddonEndSlotChanged(evt); };
        _this._targetWidthCallback = function () { return _this._adapter.getTargetWidth(); };
        _this._mousedownListener = function (evt) { return _this._onMouseDown(evt); };
        return _this;
    }
    SelectFoundation.prototype.initialize = function () {
        _super.prototype.initialize.call(this);
        _super.prototype.initializeTarget.call(this);
        this._initializeLabel();
        this._initializeAccessibility();
        this._adapter.setPlaceholderText(this._placeholder);
        this._applyDensity();
        this._applyInset();
        this._detectLeadingElement();
        this._detectAddonEndContent();
        this._adapter.addMouseDownListener(this._mousedownListener);
        this._adapter.setLeadingListener(this._leadingChangeListener);
        this._adapter.setAddonEndListener(this._addonEndChangeListener);
        if (this._disabled) {
            this._adapter.setDisabled(true);
        }
        if (this._invalid) {
            this._adapter.setInvalid(true);
        }
    };
    SelectFoundation.prototype.disconnect = function () {
        _super.prototype.disconnect.call(this);
        this._hasFocus = false;
        this._adapter.removeMouseDownListener(this._mousedownListener);
        this._adapter.removeLeadingListener(this._leadingChangeListener);
        this._adapter.removeAddonEndListener(this._leadingChangeListener);
        if (this._notchedOutlineInstance) {
            this._notchedOutlineInstance.destroy();
            this._notchedOutlineInstance = undefined;
        }
        if (this._floatingLabelInstance) {
            this._floatingLabelInstance.destroy();
            this._floatingLabelInstance = undefined;
        }
    };
    SelectFoundation.prototype._initializeLabel = function () {
        this._adapter.initializeLabel();
        this._updateLabel();
        this._notchedOutlineInstance = this._adapter.initializeNotchedOutline();
        this._floatingLabelInstance = this._adapter.initializeFloatingLabel();
        if (this._floatLabelType === 'always') {
            this._floatLabel(true);
        }
        else {
            var hasText = !!this._getSelectedText();
            this._floatLabel(hasText);
        }
    };
    SelectFoundation.prototype._destroyLabel = function () {
        if (this._notchedOutlineInstance) {
            this._notchedOutlineInstance.closeNotch();
            this._notchedOutlineInstance.destroy();
            this._notchedOutlineInstance = undefined;
        }
        if (this._floatingLabelInstance) {
            this._floatingLabelInstance.destroy();
            this._floatingLabelInstance = undefined;
        }
    };
    SelectFoundation.prototype._initializeValue = function () {
        _super.prototype._initializeValue.call(this);
        if (this._selectedValues.length) {
            this._floatLabel(true);
        }
    };
    SelectFoundation.prototype._initializeAccessibility = function () {
        this._adapter.setMultiple(this._multiple);
        if (this._required) {
            this._adapter.setHostAttribute('aria-required', 'true');
        }
        if (this._disabled) {
            this._adapter.setHostAttribute('aria-disabled', 'true');
        }
        if (this._invalid) {
            this._adapter.setHostAttribute('aria-invalid', 'true');
        }
    };
    SelectFoundation.prototype._onMouseDown = function (evt) {
        var isElementWithinSelf = this._adapter.isWithinSelf(evt.target);
        if (isElementWithinSelf) {
            evt.preventDefault();
        }
    };
    /** Called when the `slotchange` event fires on the "leading" slot element. */
    SelectFoundation.prototype._onLeadingSlotChanged = function (evt) {
        this._detectLeadingElement();
    };
    /** Called when the `slotchange` event fires on the "addon-end" slot element. */
    SelectFoundation.prototype._onAddonEndSlotChanged = function (evt) {
        this._detectAddonEndContent();
    };
    /** Updates the component state based on the existance of elements within the "leading" slot. */
    SelectFoundation.prototype._detectLeadingElement = function () {
        if (this._adapter.hasLeadingElement()) {
            this._adapter.addRootClass(SELECT_CONSTANTS.classes.LEADING_ICON);
        }
        else {
            this._adapter.removeRootClass(SELECT_CONSTANTS.classes.LEADING_ICON);
        }
    };
    SelectFoundation.prototype._detectAddonEndContent = function () {
        if (this._adapter.hasAddonEndNodes()) {
            this._adapter.addRootClass(SELECT_CONSTANTS.classes.ADDON_END);
        }
        else {
            this._adapter.removeRootClass(SELECT_CONSTANTS.classes.ADDON_END);
        }
    };
    SelectFoundation.prototype._onClick = function (evt) {
        if (this._disabled) {
            return;
        }
        // We ignore clicks events that originate from without our addon-end container
        var composedPath = getEventPath(evt);
        if (composedPath.find(function (el) { return el.classList && el.classList.contains(SELECT_CONSTANTS.classes.ADDON_END_CONTAINER); })) {
            return;
        }
        this.setFocus();
        _super.prototype._onClick.call(this, evt);
    };
    /** Handles receiving focus on the selected text element. */
    SelectFoundation.prototype._onFocus = function (evt) {
        if (this._disabled) {
            return;
        }
        this._adapter.addRootClass(SELECT_CONSTANTS.classes.FOCUSED);
        this._floatLabel(true);
        this._hasFocus = true;
        _super.prototype._onFocus.call(this, evt);
    };
    /**
     * Updates the state of the notched outline.
     * @param isNotched Whether the outline is notched or not.
     */
    SelectFoundation.prototype._notch = function (isNotched) {
        if (!this._floatingLabelInstance || !this._notchedOutlineInstance) {
            return;
        }
        if (isNotched) {
            if (this._inset) {
                return;
            }
            this._notchedOutlineInstance.notch(this._getLabelWidth());
        }
        else if (this._floatLabelType !== 'always') {
            this._notchedOutlineInstance.closeNotch();
        }
    };
    SelectFoundation.prototype._getLabelWidth = function () {
        var width = ((this._floatingLabelInstance && this._floatingLabelInstance.getWidth()) || 0) * SELECT_CONSTANTS.numbers.LABEL_SCALE;
        if (!width) {
            var labelFont = this._adapter.getLabelFontMetrics();
            var fontSize = labelFont.fontSize * SELECT_CONSTANTS.numbers.LABEL_SCALE;
            width = this._adapter.getLabelWidth(fontSize, labelFont.fontFamily);
        }
        return width;
    };
    SelectFoundation.prototype._floatLabel = function (value) {
        if (!this._floatingLabelInstance) {
            return;
        }
        if (value) {
            this._adapter.addRootClass(SELECT_CONSTANTS.classes.LABEL_FLOAT);
        }
        else {
            this._adapter.removeRootClass(SELECT_CONSTANTS.classes.LABEL_FLOAT);
        }
        if (this._floatLabelType === 'always') {
            value = true;
        }
        this._floatingLabelInstance.float(value);
        this._notch(value);
    };
    /** Handles losing focus on the selected text element. */
    SelectFoundation.prototype._onBlur = function (evt) {
        _super.prototype._onBlur.call(this, evt);
        this._setBlurred();
        this._hasFocus = false;
    };
    SelectFoundation.prototype._onDismiss = function () {
        _super.prototype._onDismiss.call(this);
        this._setBlurred();
        this._hasFocus = false;
    };
    SelectFoundation.prototype._onDropdownScrollEnd = function () {
        this._adapter.emitHostEvent(SELECT_CONSTANTS.events.SCROLLED_BOTTOM);
    };
    /**
     * Handles selecting an item in the dropdown.
     * @param {ISelectOption} option The selected option.
     * @param {number} optionIndex The index of the selected option.
     */
    SelectFoundation.prototype._onSelect = function (option, optionIndex, closeDropdown) {
        if (closeDropdown === void 0) { closeDropdown = true; }
        return __awaiter(this, void 0, void 0, function () {
            var result;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, _super.prototype._onSelect.call(this, option, optionIndex, closeDropdown)];
                    case 1:
                        result = _a.sent();
                        if (result) {
                            this._adapter.setSelectedText(this._getSelectedText());
                            if (closeDropdown && !this._multiple) {
                                this._adapter.setFocus();
                            }
                        }
                        return [2 /*return*/, result];
                }
            });
        });
    };
    /** Updates the state of the component to not contain focus. */
    SelectFoundation.prototype._setBlurred = function () {
        this._adapter.removeRootClass(SELECT_CONSTANTS.classes.FOCUSED);
        if (!this._selectedValues.length) {
            this._floatLabel(false);
        }
    };
    /** Resets the state of the component to original values. */
    SelectFoundation.prototype._reset = function () {
        _super.prototype._reset.call(this);
        this._adapter.setSelectedText('');
        this._floatLabel(false);
    };
    SelectFoundation.prototype._updateLabel = function () {
        if (this._adapter.hasLabel()) {
            this._adapter.setLabel(this._label);
        }
        if (this._label) {
            this._adapter.removeRootClass(SELECT_CONSTANTS.classes.NO_LABEL);
        }
        else {
            this._adapter.addRootClass(SELECT_CONSTANTS.classes.NO_LABEL);
        }
    };
    SelectFoundation.prototype._applyValue = function (value) {
        _super.prototype._applyValue.call(this, value);
        // Update the state of the component based on the existence of a selected value
        var text = this._getSelectedText();
        this._adapter.setSelectedText(text);
        if (!this._open) {
            this._floatLabel(!!text);
        }
    };
    SelectFoundation.prototype._applyDensity = function () {
        this._adapter.setDense(this._density === 'dense');
        this._adapter.setRoomy(this._density === 'roomy');
    };
    SelectFoundation.prototype._applyInset = function () {
        var _this = this;
        if (this._inset) {
            if (this._notchedOutlineInstance) {
                this._notchedOutlineInstance.closeNotch();
            }
        }
        else if (this._hasFocus || this._selectedValues.length) {
            window.requestAnimationFrame(function () { return _this._notch(true); }); // Need to wait a cycle before calculating new label width
        }
        this._adapter.setInset(this._inset);
    };
    SelectFoundation.prototype.setFocus = function () {
        this._adapter.setFocus();
    };
    Object.defineProperty(SelectFoundation.prototype, "label", {
        /** Gets/sets the label text. */
        get: function () {
            return this._label;
        },
        set: function (value) {
            if (this._label !== value) {
                this._label = value;
                this._updateLabel();
                if (this._label) {
                    this._initializeLabel();
                    this._adapter.setHostAttribute(SELECT_CONSTANTS.attributes.LABEL, this._label);
                }
                else {
                    this._destroyLabel();
                    this.required = false;
                    this._adapter.removeHostAttribute(SELECT_CONSTANTS.attributes.LABEL);
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(SelectFoundation.prototype, "disabled", {
        /** Gets/sets the disabled state. */
        get: function () {
            return this._disabled;
        },
        set: function (value) {
            if (this._disabled !== value) {
                this._disabled = value;
                this._adapter.setDisabled(this._disabled);
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(SelectFoundation.prototype, "invalid", {
        /** Gets/sets the invalid state. */
        get: function () {
            return this._invalid;
        },
        set: function (value) {
            if (this._invalid !== value) {
                this._invalid = value;
                this._adapter.setInvalid(this._invalid);
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(SelectFoundation.prototype, "required", {
        /** Gets/sets the required state which controls the visibility of the asterisk in the label. */
        get: function () {
            return this._required;
        },
        set: function (value) {
            var _this = this;
            if (this._required !== value) {
                this._required = value;
                if (this._notchedOutlineInstance && this._floatingLabelInstance && (this._hasFocus || this._selectedValues.length)) {
                    window.requestAnimationFrame(function () { return _this._notch(true); });
                }
                this._adapter.setRequired(this._required);
                if (this._required) {
                    this._adapter.setHostAttribute(SELECT_CONSTANTS.attributes.REQUIRED);
                }
                else {
                    this._adapter.removeHostAttribute(SELECT_CONSTANTS.attributes.REQUIRED);
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(SelectFoundation.prototype, "density", {
        /** Gets/sets the density type. */
        get: function () {
            return this._density;
        },
        set: function (value) {
            if (this._density !== value) {
                this._density = value;
                this._applyDensity();
                this._adapter.setHostAttribute(SELECT_CONSTANTS.attributes.DENSITY, isDefined(this._density) ? this._density.toString() : '');
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(SelectFoundation.prototype, "floatLabelType", {
        /** Gets/sets the floating label type. */
        get: function () {
            return this._floatLabelType;
        },
        set: function (value) {
            if (this._floatLabelType !== value) {
                this._floatLabelType = value;
                this._floatLabel(this._floatLabelType === 'always');
                this._adapter.setHostAttribute(SELECT_CONSTANTS.attributes.FLOAT_LABEL_TYPE, isDefined(this._floatLabelType) ? this._floatLabelType.toString() : '');
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(SelectFoundation.prototype, "placeholder", {
        /** Gets/sets the placeholder text. */
        get: function () {
            return this._placeholder;
        },
        set: function (value) {
            if (this._placeholder !== value) {
                this._placeholder = value;
                this._adapter.setPlaceholderText(this._placeholder);
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(SelectFoundation.prototype, "inset", {
        get: function () {
            return this._inset;
        },
        set: function (value) {
            if (this._inset !== value) {
                this._inset = value;
                this._applyInset();
                if (this._inset) {
                    this._adapter.setHostAttribute(SELECT_CONSTANTS.attributes.INSET);
                }
                else {
                    this._adapter.removeHostAttribute(SELECT_CONSTANTS.attributes.INSET);
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    return SelectFoundation;
}(BaseSelectFoundation));
export { SelectFoundation };
